import React, { ReactNode, RefObject, useRef, useEffect } from 'react';

import { clsx } from 'clsx';

import { motion, useAnimation, useInView } from 'framer-motion';

import styles from './FadeIn.scss';

type Props = {
  children: ReactNode;
  className?: string;
  delay?: number;
  id?: string;
  animationRepeat?: boolean;
  direction?: 'top' | 'right' | 'bottom' | 'left';
};

const FadeIn = ({
  children,
  className,
  delay = 0.5,
  id,
  animationRepeat = false,
  direction = 'bottom',
}: Props) => {
  const ref: RefObject<HTMLDivElement> = useRef(null);
  const control = useAnimation();
  const inViewport = useInView(ref);

  const getAnimationVariant = (direction: Props['direction']) => {
    const variants: Record<string, any> = {
      initial: { opacity: 0 },
      animate: {
        opacity: 1,
        transition: { delay, duration: 0.5 },
      },
    };

    switch (direction) {
      case 'top':
        variants.initial.y = -50;
        variants.animate.y = 0;
        break;
      case 'right':
        variants.initial.x = 50;
        variants.animate.x = 0;
        break;
      case 'bottom':
        variants.initial.y = 50;
        variants.animate.y = 0;
        break;
      case 'left':
        variants.initial.x = -50;
        variants.animate.x = 0;
        break;
      default:
        break;
    }

    return variants;
  };

  useEffect(() => {
    if (inViewport) {
      control.start('animate');
    } else {
      if (animationRepeat) {
        control.start('initial');
      }
    }
  }, [control, inViewport]);

  return (
    <motion.div
      ref={ref}
      id={id}
      className={clsx(styles.container, className)}
      variants={getAnimationVariant(direction)}
      initial="initial"
      animate={control}
    >
      {children}
    </motion.div>
  );
};

export default FadeIn;
